import { useState } from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

type ImageInfoPopoverProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  description: string;
};

const ImageInfoPopover = ({
  anchorEl,
  onClose,
  description
}: ImageInfoPopoverProps) => {
  const theme = useTheme();
  const infoDescription = description.trim();
  const isInfoOpen = Boolean(anchorEl);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (navigator.clipboard && infoDescription) {
      navigator.clipboard.writeText(infoDescription).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <Popover
      open={isInfoOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{
        paper: {
          elevation: 8,
          sx: {
            width: 360,
            maxWidth: '90vw',
            borderRadius: 2,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column'
          }
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <InfoOutlinedIcon fontSize="small" sx={{ color: 'primary.main' }} />
          <Typography variant="body2" fontWeight={600} color="text.primary">
            Image Prompt
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ px: 1, maxHeight: 300, overflowY: 'auto' }}>
        {infoDescription ? (
          <Box
            sx={{
              p: 1,
              borderRadius: 1
            }}
          >
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {infoDescription}
            </Typography>
          </Box>
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ py: 3, color: 'text.secondary' }}
          >
            <Typography variant="body2" fontStyle="italic">
              No description provided
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Footer */}
      {infoDescription && (
        <Box
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            borderTop: `1px solid ${theme.palette.divider}`
          }}
        >
          <Button
            size="small"
            startIcon={
              copied ? (
                <CheckIcon fontSize="xsmall" />
              ) : (
                <ContentCopyIcon fontSize="xsmall" />
              )
            }
            onClick={handleCopy}
            variant="outlined"
            color={copied ? 'success' : 'primary'}
            disabled={copied}
            sx={{ minWidth: 100 }}
          >
            {copied ? 'Copied' : 'Copy Text'}
          </Button>
        </Box>
      )}
    </Popover>
  );
};

export default ImageInfoPopover;
